import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import api from "../../utils/axios.config";
import Loading from "../Shared/Loading";
import { baseUrl } from "../../utils/url";
import { toast } from "react-hot-toast";
import { Edit, Trash2, Phone, Mail, Home, User, Check, Eye, Download, Briefcase, Target } from "lucide-react";
import useAdmin from "../../hooks/useAdmin";
import { Modal } from "react-responsive-modal";
import { jsPDF } from "jspdf";
import * as htmlToImage from "html-to-image";
import useLogo from "../../hooks/useLogo";


function AllMember() {
  const [editingMember, setEditingMember] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingMember, setViewingMember] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedCommittee, setSelectedCommittee] = useState("all");
  const [editingOrder, setEditingOrder] = useState({ memberId: null, value: '' });
  const [memberImageBase64, setMemberImageBase64] = useState("");
  const [memberImageError, setMemberImageError] = useState(false);
  const [logoImageError, setLogoImageError] = useState(false);

  const queryClient = useQueryClient();
  const [adminData, adminLoading] = useAdmin();
  const isAdmin = adminData?.success;
  const { data: logoUrl } = useLogo();

  const { isLoading, error, data, refetch } = useQuery({
    queryKey: ["allMember"],
    queryFn: async () => await api.get("member"),
  });

  const members = data?.data?.data;

  // Filter members by selected committee
  const filteredMembers = selectedCommittee === "all"
    ? members
    : members?.filter(member => member.committeeName === selectedCommittee);

  const committeeOptions = [
    { name: "কেন্দ্রীয়", value: "central" },
    { name: "কার্যকরী পরিষদ", value: "executive" },
    { name: "কক্সবাজার", value: "cox-bazar" },
    { name: "নোয়াখালী-লক্ষ্মীপুর", value: "noakhali-lakshmipur" },
  ];

  const organizationDesignationOptions = [
    { value: "member", label: "সদস্য" },
    { value: "advisor", label: "উপদেষ্টা" },
  ];

  const convertImageToBase64 = (imageUrl) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        try {
          const dataURL = canvas.toDataURL("image/jpeg", 0.8);
          resolve(dataURL);
        } catch (error) {
          resolve("");
        }
      };
      img.onerror = () => resolve("");
      img.src = imageUrl;
    });
  };

  const handleViewClick = async (member) => {
    setViewingMember(member);
    setShowViewModal(true);
    setMemberImageError(false);
    setLogoImageError(false);

    const imageUrl = `${baseUrl}${member.imageUrl}`;
    const base64Image = await convertImageToBase64(imageUrl);
    if (base64Image) {
      setMemberImageBase64(base64Image);
    } else {
      setMemberImageError(true);
    }
  };

  const handleDownloadPDF = async () => {
    const pdfWidth = 210;
    const pdfHeight = 297;

    const idCard = document.getElementById("memberCard");
    const downloadButton = document.getElementById("downloadMemberButton");

    if (!idCard || !downloadButton) return;

    try {
      const originalCardStyle = {
        width: idCard.style.width,
        height: idCard.style.height,
      };
      const originalButtonDisplay = downloadButton.style.display;

      idCard.style.width = `${pdfWidth}mm`;
      idCard.style.height = `${pdfHeight}mm`;
      downloadButton.style.display = "none";

      await new Promise((resolve) => setTimeout(resolve, 200));

      const dataUrl = await htmlToImage.toPng(idCard, {
        quality: 1.0,
        pixelRatio: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
      });

      const pdf = new jsPDF({
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const imgProps = pdf.getImageProperties(dataUrl);
      const aspectRatio = imgProps.width / imgProps.height;

      const imgWidth = pdfWidth;
      const imgHeight = imgWidth / aspectRatio;

      pdf.addImage(dataUrl, "PNG", 0, 0, imgWidth, imgHeight);
      pdf.save(`${viewingMember.name}-profile.pdf`);

      idCard.style.width = originalCardStyle.width;
      idCard.style.height = originalCardStyle.height;
      downloadButton.style.display = originalButtonDisplay;
    } catch (error) {
      toast.error("PDF তৈরি করতে সমস্যা হয়েছে");
      idCard.style.width = "";
      idCard.style.height = "";
      downloadButton.style.display = "";
    }
  };

  // Edit mutation
  const editMutation = useMutation({
    mutationFn: async ({ memberId, formData }) => {
      return api.put(`member/${memberId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    onSuccess: () => {
      toast.success("সদস্যের তথ্য সফলভাবে আপডেট হয়েছে");
      setShowEditModal(false);
      setEditingMember(null);
      queryClient.invalidateQueries(["allMember"]);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || "আপডেট করতে সমস্যা হয়েছে");
    },
  });

  const handleEditClick = (member) => {
    setEditingMember(member);
    setEditFormData({
      name: member.name,
      email: member.email,
      mobile: member.mobile,
      designation: member.designation,
      occupation: member.occupation || "",
      address: member.address,
      facebookUrl: member.facebookUrl || "",
      whatsappUrl: member.whatsappUrl || "",
      organizationDesignation: member.organizationDesignation,
      committeeName: member.committeeName,
      displayOrder: member.displayOrder || 0,
    });
    setSelectedImage(null);
    setPreviewUrl(`${baseUrl}${member.imageUrl}`);
    setShowEditModal(true);
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();

    const formData = new FormData();
    Object.keys(editFormData).forEach(key => {
      formData.append(key, editFormData[key]);
    });

    if (selectedImage) {
      formData.append('image', selectedImage);
    }

    editMutation.mutate({
      memberId: editingMember._id,
      formData: formData,
    });
  };

  const handleInputChange = (field, value) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleOrderChange = async (memberId, newOrder) => {
    try {
      const formData = new FormData();
      formData.append('displayOrder', newOrder);

      await api.put(`member/${memberId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      queryClient.invalidateQueries(["allMember"]);
      toast.success("ক্রম আপডেট হয়েছে");
      setEditingOrder({ memberId: null, value: '' });
    } catch (error) {
      toast.error("ক্রম আপডেট করতে সমস্যা হয়েছে");
    }
  };

  const handleDeleteMember = async (id) => {
    const confirmDelete = window.confirm(
      "আপনি কি নিশ্চিত যে আপনি এই সদস্যকে মুছে ফেলতে চান?"
    );
    if (!confirmDelete) return;

    try {
      const { data } = await api.delete(`member/${id}`);
      if (data.success) {
        toast.success(data.message);
        refetch();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error("সদস্য মুছতে সমস্যা হয়েছে");
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  if (error) {
    return (
      <div className="text-center py-10 text-red-500">
        Error loading members
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-2 py-4">
      <h2 className="text-xl md:text-2xl font-bold text-center text-primary mb-4">
        <User className="inline mr-2" size={24} />
        সকল কার্যকরী সদস্য
      </h2>

      {/* Committee Filter */}
      <div className="mb-4 flex justify-center">
        <select
          value={selectedCommittee}
          onChange={(e) => setSelectedCommittee(e.target.value)}
          className="select select-bordered w-64"
        >
          <option value="all">সকল কমিটি</option>
          {committeeOptions.map(option => (
            <option key={option.value} value={option.value}>{option.name}</option>
          ))}
        </select>
      </div>

      {filteredMembers?.length === 0 ? (
        <div className="text-center py-10 text-gray-500">কোন সদস্য পাওয়া যায়নি</div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {!adminLoading && isAdmin && (
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      ক্রম
                    </th>
                  )}
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    সদস্য
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    পদবী
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    যোগাযোগ
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    কমিটি
                  </th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredMembers?.map((member) => (
                  <tr key={member._id} className="hover:bg-gray-50">
                    {!adminLoading && isAdmin && (
                      <td className="px-2 py-2 whitespace-nowrap">
                        <div className="flex items-center">
                          <input
                            type="number"
                            value={editingOrder.memberId === member._id ? editingOrder.value : member.displayOrder || 0}
                            onChange={(e) => setEditingOrder({ memberId: member._id, value: e.target.value })}
                            className="input input-bordered w-16 text-xs"
                            min="0"
                          />
                          {editingOrder.memberId === member._id && (
                            <button
                              className="btn btn-xs btn-ghost ml-1"
                              onClick={() => handleOrderChange(member._id, editingOrder.value)}
                            >
                              <Check size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <img
                            className="h-10 w-10 rounded-full object-cover"
                            src={`${baseUrl}${member.imageUrl}`}
                            alt={member.name}
                          />
                        </div>
                        <div className="ml-2">
                          <div className="text-sm font-medium text-gray-900">
                            {member.name}
                          </div>
                          <div className="text-xs text-gray-500 flex items-center">
                            <Home className="mr-1" size={12} />
                            {member.address}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-sm text-gray-900">
                        {member.designation}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-xs text-gray-900 flex items-center">
                        <Phone className="mr-1" size={12} />
                        {member.mobile}
                      </div>
                      <div className="text-xs text-gray-500 flex items-center">
                        <Mail className="mr-1" size={12} />
                        {member.email}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {committeeOptions.find(opt => opt.value === member.committeeName)?.name || member.committeeName}
                      </span>
                      <div className="text-xs text-gray-500 capitalize">
                        {member.organizationDesignation === 'member' ? 'সদস্য' : 'উপদেষ্টা'}
                      </div>
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        className="text-blue-600 hover:text-blue-900 mr-2"
                        onClick={() => handleViewClick(member)}
                        title="সদস্যের সম্পূর্ণ তথ্য দেখুন"
                      >
                        <Eye size={16} />
                      </button>
                      {!adminLoading && isAdmin && (
                        <>
                          <button
                            className="text-green-600 hover:text-green-900 mr-2"
                            onClick={() => handleEditClick(member)}
                            title="সদস্যের তথ্য সম্পাদনা করুন"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            className="text-red-600 hover:text-red-900"
                            onClick={() => handleDeleteMember(member._id)}
                            title="সদস্য মুছে ফেলুন"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && viewingMember && (
        <Modal
          classNames={{
            modal: "customModal md:w-full w-[90%] max-w-3xl",
            overlay: "customOverlay",
          }}
          open={showViewModal}
          onClose={() => setShowViewModal(false)}
          center
        >
          <div id="memberCard" className="bg-white p-6 rounded-lg">
            <div className="flex flex-col items-center mb-6">
              <div className="flex items-center justify-center mb-4">
                {logoUrl && !logoImageError ? (
                  <img
                    src={logoUrl}
                    className="w-16 h-16 mr-3"
                    alt="Logo"
                    onError={() => setLogoImageError(true)}
                  />
                ) : (
                  <div className="w-16 h-16 mr-3 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold">
                    NNS
                  </div>
                )}
                <div>
                  <h2 className="text-xl font-bold text-center">
                    নিঃস্বার্থ নবজীবন সংগঠন (NNS)
                  </h2>
                  <p className="text-sm text-gray-600 text-center">
                    সদস্য প্রোফাইল
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-start gap-6 mb-6">
              <div className="flex-shrink-0">
                {memberImageBase64 && !memberImageError ? (
                  <img
                    src={memberImageBase64}
                    alt={viewingMember.name}
                    className="w-32 h-32 rounded-lg object-cover border-4 border-primary"
                    onError={() => setMemberImageError(true)}
                  />
                ) : (
                  <div className="w-32 h-32 rounded-lg border-4 border-primary bg-primary text-white flex items-center justify-center text-4xl font-bold">
                    {viewingMember.name?.charAt(0)?.toUpperCase() || 'NNS'}
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-3">
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">{viewingMember.name}</h3>
                  <p className="text-lg text-primary font-semibold">{viewingMember.designation}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-start">
                    <Phone className="mr-2 text-gray-600 mt-1" size={18} />
                    <div>
                      <p className="text-xs text-gray-500">মোবাইল</p>
                      <p className="text-sm font-medium">{viewingMember.mobile}</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <Mail className="mr-2 text-gray-600 mt-1" size={18} />
                    <div>
                      <p className="text-xs text-gray-500">ইমেইল</p>
                      <p className="text-sm font-medium break-all">{viewingMember.email}</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <Home className="mr-2 text-gray-600 mt-1" size={18} />
                    <div>
                      <p className="text-xs text-gray-500">ঠিকানা</p>
                      <p className="text-sm font-medium">{viewingMember.address}</p>
                    </div>
                  </div>

                  {viewingMember.occupation && (
                    <div className="flex items-start">
                      <User className="mr-2 text-gray-600 mt-1" size={18} />
                      <div>
                        <p className="text-xs text-gray-500">পেশা</p>
                        <p className="text-sm font-medium">{viewingMember.occupation}</p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-start">
                    <Briefcase className="mr-2 text-gray-600 mt-1" size={18} />
                    <div>
                      <p className="text-xs text-gray-500">কমিটি</p>
                      <p className="text-sm font-medium">
                        {committeeOptions.find(opt => opt.value === viewingMember.committeeName)?.name}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <Target className="mr-2 text-gray-600 mt-1" size={18} />
                    <div>
                      <p className="text-xs text-gray-500">পদ</p>
                      <p className="text-sm font-medium">
                        {viewingMember.organizationDesignation === 'member' ? 'সদস্য' : 'উপদেষ্টা'}
                      </p>
                    </div>
                  </div>
                </div>

                {(viewingMember.facebookUrl || viewingMember.whatsappUrl) && (
                  <div className="pt-3 border-t">
                    <p className="text-xs text-gray-500 mb-2">সোশ্যাল মিডিয়া</p>
                    <div className="flex gap-2">
                      {viewingMember.facebookUrl && (
                        <a
                          href={viewingMember.facebookUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          Facebook
                        </a>
                      )}
                      {viewingMember.whatsappUrl && (
                        <a
                          href={viewingMember.whatsappUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-600 hover:text-green-800 text-sm"
                        >
                          WhatsApp
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-center mt-6 pt-4 border-t">
              <button
                id="downloadMemberButton"
                onClick={handleDownloadPDF}
                className="btn bg-primary hover:bg-primary-focus text-white flex items-center gap-2"
              >
                <Download size={18} />
                PDF ডাউনলোড করুন
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Modal */}
      {showEditModal && editingMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">সদস্যের তথ্য সম্পাদনা</h3>
            <form onSubmit={handleEditSubmit}>
              {/* Profile Picture Section */}
              <div className="mb-6 text-center">
                <div className="relative inline-block">
                  <img
                    src={previewUrl}
                    alt="Profile Preview"
                    className="w-24 h-24 rounded-full object-cover border-4 border-gray-200"
                  />
                  <label className="absolute bottom-0 right-0 bg-blue-500 text-white rounded-full p-2 cursor-pointer hover:bg-blue-600">
                    <Edit size={16} />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>
                </div>
                <p className="text-sm text-gray-500 mt-2">প্রোফাইল ছবি পরিবর্তন করতে ক্লিক করুন</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input type="text" value={editFormData.name} onChange={(e) => handleInputChange('name', e.target.value)} placeholder="Name" className="input input-bordered" />
                <input type="text" value={editFormData.email} onChange={(e) => handleInputChange('email', e.target.value)} placeholder="Email" className="input input-bordered" />
                <input type="text" value={editFormData.mobile} onChange={(e) => handleInputChange('mobile', e.target.value)} placeholder="Mobile" className="input input-bordered" />
                <input type="text" value={editFormData.designation} onChange={(e) => handleInputChange('designation', e.target.value)} placeholder="Designation" className="input input-bordered" />
                <input type="text" value={editFormData.occupation} onChange={(e) => handleInputChange('occupation', e.target.value)} placeholder="Occupation" className="input input-bordered" />
                <input type="text" value={editFormData.address} onChange={(e) => handleInputChange('address', e.target.value)} placeholder="Address" className="input input-bordered" />
                <input type="text" value={editFormData.facebookUrl} onChange={(e) => handleInputChange('facebookUrl', e.target.value)} placeholder="Facebook URL" className="input input-bordered" />
                <input type="text" value={editFormData.whatsappUrl} onChange={(e) => handleInputChange('whatsappUrl', e.target.value)} placeholder="WhatsApp URL" className="input input-bordered" />
                <select value={editFormData.organizationDesignation} onChange={(e) => handleInputChange('organizationDesignation', e.target.value)} className="select select-bordered">
                  {organizationDesignationOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <select value={editFormData.committeeName} onChange={(e) => handleInputChange('committeeName', e.target.value)} className="select select-bordered">
                  {committeeOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.name}</option>
                  ))}
                </select>
                <input type="number" value={editFormData.displayOrder} onChange={(e) => handleInputChange('displayOrder', e.target.value)} placeholder="Display Order" className="input input-bordered" />
              </div>
              <div className="flex justify-end mt-4">
                <button type="button" onClick={() => setShowEditModal(false)} className="btn btn-ghost mr-2">Cancel</button>
                <button type="submit" className="btn btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AllMember;
